import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { useNavigate } from "react-router";
import DashboardLayout from "../layouts/dashboard";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import SearchField from "../../interface/shared/searchfield";
import { ProviderStock, useStock } from "../../api/stock";
import { FadeIn } from "../../interface/shared/animations";


export default function Stock() {
    return (
        <DashboardLayout>
            <ProviderStock>
                <StockContent />
            </ProviderStock>
        </DashboardLayout>
    );
}

function StockContent() {
    const { stock, errors, loading, fetchStock, page, perPage, total, setPage, setPerPage, searchTerm, setSearchTerm, sortField, sortOrder, setSort } = useStock();
    const navigate = useNavigate();

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography variant="h5" gutterBottom>
                        Stock
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        All current stock available in the warehouse.
                    </Typography>
                </Box>
                <SearchField
                    placeholder="Search stock by name, product code, main code or stock location"
                    value={searchTerm}
                    onChange={setSearchTerm}
                />
            </Stack>
            {errors.length > 0 && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={fetchStock}>
                            Retry
                        </Button>
                    }
                >
                    {errors.join(" ")}
                </Alert>
            )}
            <Card variant="outlined">
                <CardContent>
                    {loading ? (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Code</TableCell>
                                    <TableCell>Product</TableCell>
                                    <TableCell>Stock Location</TableCell>
                                    <TableCell>Level</TableCell>
                                    <TableCell>Quantity</TableCell>
                                    <TableCell>Weight</TableCell>
                                    <TableCell>Expires At</TableCell>
                                    <TableCell align="right"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell align="right">
                                            <Box alignItems="center" justifyContent="end" display="flex">
                                                <Skeleton variant="rounded" width={70} height={30} />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <FadeIn>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Code</TableCell>
                                        <TableCell>Product</TableCell>
                                        <TableCell>Stock Location</TableCell>
                                        <TableCell>Level</TableCell>
                                        <TableCell sortDirection={sortField === "quantity" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "quantity"}
                                                direction={sortField === "quantity" ? sortOrder : "asc"}
                                                onClick={() => setSort("quantity")}
                                            >
                                                Quantity
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sortDirection={sortField === "weight" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "weight"}
                                                direction={sortField === "weight" ? sortOrder : "asc"}
                                                onClick={() => setSort("weight")}
                                            >
                                                Weight
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sortDirection={sortField === "expires_at" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "expires_at"}
                                                direction={sortField === "expires_at" ? sortOrder : "asc"}
                                                onClick={() => setSort("expires_at")}
                                            >
                                                Expires At
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {stock.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8}>
                                                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                                                    No stock found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : stock.map((stock) => (
                                        <TableRow key={`${stock.id}`}>
                                            <TableCell>{stock.product.code}</TableCell>
                                            <TableCell>{stock.product.name}</TableCell>
                                            <TableCell>{stock.location.name}</TableCell>
                                            <TableCell>{stock.level}</TableCell>
                                            <TableCell>{stock.quantity}</TableCell>
                                            <TableCell>{stock.weight}</TableCell>
                                            <TableCell>{stock.expires_at != null ? stock.expires_at.toLocaleDateString() : '-'}</TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    endIcon={<VisibilityRoundedIcon fontSize="small" />}
                                                    onClick={() => navigate(`/stock/${stock.id}`)}
                                                >
                                                    View
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page - 1}
                                onPageChange={(_, newPage) => setPage(newPage + 1)}
                                rowsPerPage={perPage}
                                onRowsPerPageChange={(e) => {
                                    setPerPage(parseInt(e.target.value, 10));
                                    setPage(1);
                                }}
                                rowsPerPageOptions={[10, 15, 25, 50]}
                            />
                        </FadeIn>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}